import { motion } from "framer-motion";
import { MapPin, ExternalLink, Clock, Star } from "lucide-react";
import { OFFICES, CONTACT } from "../../lib/landingData";
import { useLang } from "../../i18n/LanguageContext";

const OfficeCard = ({ label, kicker, office, hours, directions, review, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className="rounded-[4px] border border-[var(--hairline)] overflow-hidden bg-[var(--card)]"
    data-testid={testId}
  >
    <div className="relative h-[260px] sm:h-[300px]">
      <iframe
        title={label}
        src={office.embed}
        className="absolute inset-0 h-full w-full grayscale-[0.2]"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        style={{ border: 0 }}
      />
    </div>
    <div className="flex items-start justify-between gap-4 p-5">
      <div className="flex items-start gap-3 min-w-0">
        <MapPin className="h-5 w-5 text-terra shrink-0 mt-0.5" strokeWidth={1.8} />
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted-ink)] font-semibold">{kicker}</div>
          <div className="font-display text-lg text-[var(--ink)] leading-tight mt-0.5">{label}</div>
          <div className="text-sm text-[var(--muted-ink)] mt-1 leading-snug" data-testid={`${testId}-address`}>{office.query}</div>
          {hours && (
            <div className="mt-2 flex items-center gap-1.5 text-sm text-[var(--muted-ink)]" data-testid={`${testId}-hours`}>
              <Clock className="h-3.5 w-3.5" strokeWidth={1.8} /> {hours}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-3 shrink-0">
        <a
          href={office.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-terra hover:text-terra-dark transition-colors"
        >
          <span>{directions}</span>
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
        {review && (
          <a
            href={CONTACT.reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-review`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--muted-ink)] hover:text-[var(--ink)] transition-colors"
          >
            <Star className="h-3.5 w-3.5" /> <span>{review}</span>
          </a>
        )}
      </div>
    </div>
  </motion.div>
);

export const Locations = () => {
  const { t } = useLang();
  const bl = t.brand.locations;
  return (
    <section id="locations" className="relative py-24 sm:py-32 bg-[var(--page)]">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mb-14"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-terra font-semibold">{t.locations.overline}</span>
          <h2 className="mt-5 font-display text-[var(--ink)] font-semibold tracking-tight leading-[1.05]
                         text-3xl sm:text-4xl lg:text-5xl">
            {t.locations.title}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          <OfficeCard label={t.locations.nadi} kicker={bl.registered} office={OFFICES.nadi} hours={CONTACT.hours} directions={t.locations.directions} review={t.brand.review} testId="office-nadi" />
          <OfficeCard label={t.locations.suva} kicker={bl.byAppointment} office={OFFICES.suva} directions={t.locations.directions} testId="office-suva" />
        </div>
      </div>
    </section>
  );
};
